import { create } from 'zustand';
import { getPreferences, updatePreferenceDirect } from '../hooks/usePreferences';
import { addToast as showToast, ToastType } from './toastStore';

export type LogLevel = 'info' | 'success' | 'warning' | 'error';

export interface LogMessage {
  id: string;
  timestamp: number;
  message: string;
  level: LogLevel;
}

interface LiveLogState {
  messages: LogMessage[];
  maxMessages: number;
  isVisible: boolean;

  // Actions
  addLog: (message: string, level?: LogLevel) => void;
  clearLogs: () => void;
  setVisible: (visible: boolean) => void;
  toggleVisible: () => void;
}

// Load visibility state from server preferences
const loadVisibility = (): boolean => {
  const prefs = getPreferences();
  return prefs.liveLogVisible ?? false; // Default: hidden
};

export const useLiveLogStore = create<LiveLogState>((set) => ({
  messages: [],
  maxMessages: 5, // Show last 5 messages
  isVisible: loadVisibility(),

  addLog: (message: string, level: LogLevel = 'info') => {
    const newMessage: LogMessage = {
      id: `${Date.now()}_${Math.random()}`,
      timestamp: Date.now(),
      message,
      level,
    };

    set((state) => ({
      messages: [...state.messages, newMessage].slice(-state.maxMessages),
    }));
  },

  clearLogs: () => set({ messages: [] }),

  setVisible: (visible: boolean) => {
    updatePreferenceDirect('liveLogVisible', visible);
    set({ isVisible: visible });
  },

  toggleVisible: () => {
    set((state) => {
      const newVisible = !state.isVisible;
      updatePreferenceDirect('liveLogVisible', newVisible);
      return { isVisible: newVisible };
    });
  },
}));

// Helper function to add logs from anywhere
// Also shows as toast notification for visibility
export const addLog = (message: string, level: LogLevel = 'info') => {
  useLiveLogStore.getState().addLog(message, level);
  // Show as toast (LogLevel maps directly to ToastType)
  showToast(level as ToastType, message);
};
